/* eslint-disable string-to-lingui/t-call-in-function */
import {t} from '@lingui/macro'

import {PlanType} from '~/api/types/plan'
import {getFeatureCountryConfiguration} from '~/country-configuration/configuration'
import {Feature} from '~/country-configuration/types'
import Country from '~/locale/country'
import {CurrencyFormat, formatCurrency} from '~/locale/currency'
import {formatNumber} from '~/locale/helpers'

import type {PlanBenefit} from './plan-benefits-list'

export const benefitsCategories = {
    digitizedExpenses: t`Digitized Expenses`,
    spendingVisibility: t`Spending Visibility`,
    support: t`Support`,
    fullSpendingControl: t`Full spending control`,
    allInOneSpendManagement: t`All-in-one spend management`,
    liveSupport: t`Live support`,
    multiCountrySpendManagement: t`Multi-country spend management`,
    customConfiguration: t`Custom configuration`,
    dedicatedSupport: t`Dedicated support`,
    addOns: t`Add-ons`,
    automatedInvoiceManagement: t`Automated invoice management`,
    legacy: 'LEGACY',
}

const FX_FEE_STARTER = formatNumber(2.49)
const FX_FEE_ESSENTIAL_AND_ADVANCED = formatNumber(1.99)
const INVOICES_STARTER = 5
const INVOICES_ESSENTIAL = 20
const INVOICES_ADVANCED = 100

export const getCashbackAmount = (planType: PlanType): string => {
    switch (planType) {
        case PlanType.ESSENTIAL:
            return '0.5'
        case PlanType.ADVANCED:
            return '1'
        default:
            return ''
    }
}

export const getChooseFreeBenefits = () => {
    return {
        [benefitsCategories.legacy]: [
            {text: t`Pleo cards for your team`},
            {text: t`Automatic account assignment`},
            {text: t`Automatic email receipt finder`},
            {text: t`External bookkeeper access`},
            {text: t`Accounting integrations`},
            {text: t`Apple Pay & GooglePay`},
        ],
    }
}

export const getChooseTeamBenefits = (country?: Country) => {
    const {invoicesEnabled} = getFeatureCountryConfiguration(Feature.Paywall, country)

    return {
        [benefitsCategories.legacy]: [
            {text: t`Individual spending limits`},
            invoicesEnabled ? {text: t`Pay and process invoices`} : null,
            {text: t`Reimburse out-of-pocket spending`},
            {text: t`Cash expenses, per-diem & mileage`},
            {text: t`Review spend per team`},
            {text: t`Analytics`},
            {text: t`Onboarding assistance`},
        ].filter(Boolean) as PlanBenefit[],
    }
}

export const getChooseBusinessBenefits = () => {
    return {
        [benefitsCategories.legacy]: [
            {text: t`Unlimited users`},
            {text: t`Customer success manager`},
            {text: t`Personalised onboarding`},
            {text: t`Business travel insurance (extra cost)`},
        ],
    }
}

export const getStarterPlanBenefits = (country?: Country): {[key: string]: PlanBenefit[]} => {
    if (country === 'GB' || country === 'DE') {
        return getUKAndGermanStarterPlan(country)
    } else if (country === 'ES') {
        return getSpainStarterPlan()
    }

    return getEuropeStarterPlan()
}

export const getEssentialPlanBenefits = (country?: Country): {[key: string]: PlanBenefit[]} => {
    if (country === 'GB' || country === 'DE') {
        return getUKAndGermanEssentialPlan(country)
    } else if (country === 'ES') {
        return getSpainEssentialPlan()
    }

    return getEuropeEssentialPlan()
}

export const getAdvancedPlanBenefits = (country?: Country): {[key: string]: PlanBenefit[]} => {
    if (country === 'GB' || country === 'DE') {
        return getUKAndGermanAdvancedPlan(country)
    } else if (country === 'ES') {
        return getSpainAdvancedPlan()
    }

    return getEuropeAdvancedPlan()
}

const getSpainStarterPlan = (): {[key: string]: PlanBenefit[]} => {
    const bookkeeperPrice = formatCurrency(9, 'EUR', {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    const atmWithdrawalPrice = formatCurrency(5, 'EUR', {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    return {
        [benefitsCategories.digitizedExpenses]: [
            {text: t`Pleo company cards (plastic/virtual)`},
            {text: t`Automated expense reports`},
            {text: t`Accounting system integrations`},
        ],

        [benefitsCategories.spendingVisibility]: [{text: t`Real-time expense tracking`}],
        [benefitsCategories.support]: [
            {text: t`Pleo Help Center and FAQs`},
            {text: t`Onboarding Webinars`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (${bookkeeperPrice} / month)`},
            {text: t`ATM withdrawals (${atmWithdrawalPrice} / withdrawal)`},
            {text: t`Foreign currency card transactions (${FX_FEE_STARTER}% FX fee)`},
        ],
    }
}

const getSpainEssentialPlan = (): {[key: string]: PlanBenefit[]} => {
    return {
        [benefitsCategories.fullSpendingControl]: [
            {text: t`Spend limit per card or purchase`},
            {text: t`Spend analytics dashboard`},
            {text: t`Assign teams and reviewers`},
        ],
        [benefitsCategories.allInOneSpendManagement]: [
            {text: t`Reimburse employees with or without Pleo cards`},
            {text: t`Reimburse mileage and per-diem expenses`},
            {text: t`Subscriptions overview`},
        ],
        [benefitsCategories.liveSupport]: [{text: t`Email and chat support`}],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (free)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}

const getSpainAdvancedPlan = (): {[key: string]: PlanBenefit[]} => {
    return {
        [benefitsCategories.multiCountrySpendManagement]: [{text: t`Manage unlimited entities`}],
        [benefitsCategories.customConfiguration]: [{text: t`Open API access`}],
        [benefitsCategories.dedicatedSupport]: [
            {text: t`Dedicated Customer Success Manager`},
            {text: t`Phone support`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (included)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}

const getEuropeStarterPlan = (): {[key: string]: PlanBenefit[]} => {
    const bookkeeperPrice = formatCurrency(9, 'EUR', {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    const atmWithdrawalPrice = formatCurrency(5, 'EUR', {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    return {
        [benefitsCategories.digitizedExpenses]: [
            {text: t`Pleo company cards (plastic/virtual)`},
            {text: t`Automated expense reports`},
            {text: t`Accounting system integrations`},
        ],
        [benefitsCategories.spendingVisibility]: [{text: t`Real-time expense tracking`}],
        [benefitsCategories.support]: [
            {text: t`Pleo Help Center and FAQs`},
            {text: t`Onboarding Webinars`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (${bookkeeperPrice} / month)`},
            {text: t`ATM withdrawals (${atmWithdrawalPrice} / withdrawal)`},
            {text: t`Foreign currency card transactions (${FX_FEE_STARTER}% FX fee)`},
        ],
    }
}

const getEuropeEssentialPlan = (): {[key: string]: PlanBenefit[]} => {
    return {
        [benefitsCategories.fullSpendingControl]: [
            {text: t`Spend limit per card or purchase`},
            {text: t`Spend analytics dashboard`},
            {text: t`Assign teams and reviewers`},
        ],
        [benefitsCategories.allInOneSpendManagement]: [
            {text: t`Reimburse employees with or without Pleo cards`},
            {text: t`Reimburse mileage and per-diem expenses`},
            {text: t`Subscriptions overview`},
        ],
        [benefitsCategories.liveSupport]: [{text: t`Email and chat support`}],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (free)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}

const getEuropeAdvancedPlan = (): {[key: string]: PlanBenefit[]} => {
    return {
        [benefitsCategories.multiCountrySpendManagement]: [{text: t`Manage unlimited entities`}],
        [benefitsCategories.customConfiguration]: [{text: t`Open API access`}],
        [benefitsCategories.dedicatedSupport]: [
            {text: t`Dedicated Customer Success Manager`},
            {text: t`Phone support`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (included)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}

const getUKAndGermanStarterPlan = (country: Country): {[key: string]: PlanBenefit[]} => {
    const currency = country === Country.GB ? 'GBP' : 'EUR'

    const bookkeeperPrice = formatCurrency(9, currency, {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    const atmWithdrawalPrice = formatCurrency(5, currency, {
        format: CurrencyFormat.FixedFractionDigits(0),
    })

    const invoicePrice = formatCurrency(5, currency, {
        format: CurrencyFormat.FixedFractionDigits(0),
    })
    return {
        [benefitsCategories.digitizedExpenses]: [
            {text: t`Pleo company cards (plastic/virtual)`},
            {text: t`Automated expense reports`},
            {text: t`Accounting system integrations`},
        ],
        [benefitsCategories.automatedInvoiceManagement]: [
            {text: t`Manage, schedule and pay invoice`},
            {
                text: t`${INVOICES_STARTER} free invoice payments per month (${invoicePrice} per every additional invoice)`,
            },
        ],
        [benefitsCategories.spendingVisibility]: [{text: t`Real-time expense tracking`}],
        [benefitsCategories.support]: [
            {text: t`Pleo Help Center and FAQs`},
            {text: t`Onboarding Webinars`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (${bookkeeperPrice} / month)`},
            {text: t`ATM withdrawals (${atmWithdrawalPrice} / withdrawal)`},
            {text: t`Foreign currency card transactions (${FX_FEE_STARTER}% FX fee)`},
        ],
    }
}

const getUKAndGermanEssentialPlan = (country: Country): {[key: string]: PlanBenefit[]} => {
    const currency = country === Country.GB ? 'GBP' : 'EUR'

    const invoicePrice = formatCurrency(2.5, currency, {
        format: CurrencyFormat.FixedFractionDigits(1),
    })

    return {
        [benefitsCategories.fullSpendingControl]: [
            {text: t`Spend limit per card or purchase`},
            {text: t`Spend analytics dashboard`},
            {text: t`Assign teams and reviewers`},
        ],
        [benefitsCategories.allInOneSpendManagement]: [
            {text: t`Reimburse employees with or without Pleo cards`},
            {text: t`Reimburse mileage and per-diem expenses`},
            {text: t`Subscriptions overview`},
            {
                text: t`${INVOICES_ESSENTIAL} free invoice payments per month (${invoicePrice} per every additional invoice)`,
            },
        ],
        [benefitsCategories.liveSupport]: [{text: t`Email and chat support`}],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (free)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}

const getUKAndGermanAdvancedPlan = (country: Country): {[key: string]: PlanBenefit[]} => {
    const currency = country === Country.GB ? 'GBP' : 'EUR'

    const invoicePrice = formatCurrency(1.5, currency, {
        format: CurrencyFormat.FixedFractionDigits(1),
    })
    return {
        [benefitsCategories.multiCountrySpendManagement]: [
            {text: t`Manage unlimited entities`},
            {
                text: t`${INVOICES_ADVANCED} free invoice payments per month (${invoicePrice} per every additional invoice)`,
            },
        ],
        [benefitsCategories.customConfiguration]: [{text: t`Open API access`}],
        [benefitsCategories.dedicatedSupport]: [
            {text: t`Dedicated Customer Success Manager`},
            {text: t`Phone support`},
        ],
        [benefitsCategories.addOns]: [
            {text: t`External bookkeeper access (included)`},
            {text: t`ATM withdrawals (free)`},
            {
                text: t`Foreign currency card transactions (${FX_FEE_ESSENTIAL_AND_ADVANCED}% FX fee)`,
            },
        ],
    }
}
